import { Service, computed, inject } from '@angular/core';
import { slugDeServico } from '../../../shared/utils/slug.util';
import { CategoriaServicosItem, CategoriaServicosTipo } from '../models/categoria-servicos.model';
import { CategoriaPublicaResponse } from '../models/todos-servicos.model';
import { CatalogoPublicoService } from './catalogo-publico.service';
import { corDaCategoria, svgDaCategoria } from './identidade-categoria.util';

const ICONES = 'assets/images/icones-servicos';

/**
 * Ícone de card para categoria sem chave, ou com uma fora do catálogo do adm. Neutro de
 * propósito: o de "serviços digitais", que aquela tela usa, diria o que a categoria não é.
 */
const ICONE_PADRAO = `${ICONES}/icon-atendimento-cidadao.svg`;

const DESCRICAO_AUSENTE = 'Descrição não informada.';

/**
 * Rótulo do tipo da categoria → etiqueta do card.
 *
 * Espelha `TipoCategoriaServico.TiposValidos` do back-end, um conjunto fechado pelo CHECK de
 * `des_tipo_tcg`. Rótulo fora daqui é ignorado. Não há `Cadastro` no domínio: a etiqueta
 * `cadastro` do portal só existe nos exemplos da homepage.
 */
const TIPO_POR_ROTULO: ReadonlyMap<string, CategoriaServicosTipo> = new Map([
  ['Presencial', 'presencial'],
  ['Digital', 'digital'],
]);

/** Uma categoria da tela: o card, mais o total que o filtro mostra. */
export interface CategoriaPublicaItem extends CategoriaServicosItem {
  /** Serviços publicáveis da categoria (`numTotalServicos`), para o filtro. */
  readonly totalServicos: number;
}

/**
 * As categorias publicadas do órgão — a tela `/categorias-servicos`.
 *
 * Não faz chamada própria: deriva do menu do `CatalogoPublicoService` (`GET
 * publico/categoria`), a mesma leitura de que "Todos os Serviços" depende. O clique no card
 * leva para lá, e o catálogo não é carregado de novo.
 *
 * A tela depende **só** do menu: carrega enquanto ele carrega e falha quando ele falha. Os
 * grupos de serviços não entram nela, e a carga ou a falha deles não a segura.
 */
@Service()
export class CategoriasPublicasService {
  private readonly catalogo = inject(CatalogoPublicoService);

  readonly carregando = this.catalogo.carregandoMenu;

  readonly falhou = this.catalogo.falhou;

  /** Uma por categoria publicada, na ordem editorial — inclusive a que não tem serviço. */
  readonly categorias = computed<readonly CategoriaPublicaItem[]>(() =>
    [...this.catalogo.menu()].sort((a, b) => a.numOrdemCtg - b.numOrdemCtg).map(paraItem)
  );

  /** Refaz as chamadas do catálogo — o "Tentar novamente" da tela. */
  recarregar(): void {
    this.catalogo.recarregar();
  }
}

function paraItem(categoria: CategoriaPublicaResponse): CategoriaPublicaItem {
  return {
    id: String(categoria.codCategoriaServicoCtg),
    titulo: categoria.desNomeCtg,
    descricao: categoria.desDescricaoCtg?.trim() || DESCRICAO_AUSENTE,
    cor: corDaCategoria(categoria.desCorTemaCtg),
    tipos: tiposDoCard(categoria.tipos ?? []),
    icone: svgDaCategoria(categoria.desIconeCtg, ICONE_PADRAO),
    // "Todos os Serviços" aberto na categoria, pelo NOME (decisão de 29/09/2026): a tela
    // resolve o slug na lista que já carregou, e o back-end continua recebendo só código.
    rotaPagina: `servicos/${slugDeServico(categoria.desNomeCtg)}`,
    totalServicos: categoria.numTotalServicos,
  };
}

/** A ordem das etiquetas é a do card (`CATEGORIA_SERVICOS_TIPOS`), não a cadastrada. */
function tiposDoCard(rotulos: readonly string[]): readonly CategoriaServicosTipo[] {
  return rotulos
    .map((rotulo) => TIPO_POR_ROTULO.get(rotulo))
    .filter((tipo): tipo is CategoriaServicosTipo => tipo !== undefined);
}
